import {
  useContext,
  useState,
  useSelector,
  useEffect,
  useRef,
} from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import { useGetStatesForEnrollmentQuery } from "@/service/APIService";
import {
  Heading,
  Button,
  InputField,
  RadioButton,
  Spinner,
  ErrorMessage,
  Checkbox,
} from "@/common/components";
import ContactInformation from "../Common/ContactInformation";
import { validate } from "@/utils/Validations";
import { setSpouseDetailsInStore } from "@/store/ownersSlice";
import { useUpdateProductOwnershipJointAnnuitantTransactionMutation } from "@/service/OwnerService";
import { encryptData } from "@/utils/Utilities";

const SpouseDetailsProjection = () => {
  const {
    labels,
    cancel,
    dispatch,
    debounceErrors,
    debounceState,
    accessorPrivileges,
  } = useContext(PrivateContext);

  const selectedOwnerDataFromStore = useSelector(
    (state) => state?.ownersReducer?.selectedOwner
  );
  const editProjectionsResponseFromStore = useSelector(
    (state) => state?.ownersReducer?.editProjectionsResponse
  );
  const spouseDetailsFromStore = useSelector(
    (state) => state?.ownersReducer?.spouseDetails
  );

  const {
    data: states,
    isFetching: isFetching1,
    error: error1,
  } = useGetStatesForEnrollmentQuery();

  const [contactInfo, setContactInfo] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [spouseTaxId, setSpouseTaxId] = useState("");
  const [civilUnion, setCivilUnion] = useState(false);
  const [selectedOwnerDetails, setSelectedOwnerDetails] = useState();
  const [genderCode, setGenderCode] = useState("M");
  const [updateSpouseDetails, setUpdateSpouseDetails] = useState();
  const [spouseZipCode, setSpouseZipCode] = useState("");
  const [spouseEmail, setSpouseEmail] = useState("");
  const [hasTaxIdChanged, setHasTaxIdChanged] = useState(false);
  const [maskedSpouseTaxId, setMaskedSpouseTaxId] = useState("");

  const hasClearedMaskedTaxId = useRef(false);

  const [
    updateProductOwnershipJointAnnuitantApi,
    { isLoading: isFetching2, error: error2 },
  ] = useUpdateProductOwnershipJointAnnuitantTransactionMutation();

  const genderData = {
    header: labels?.ownerInquiry?.spousalGender,
    data: [
      {
        name: "Male",
        label: labels?.ownerInquiry?.male,
        id: "M",
      },
      {
        name: "Female",
        label: labels?.ownerInquiry?.female,
        id: "F",
      },
    ],
  };

  const commonProps = {
    errors,
    setErrors,
    touched,
    setTouched,
    debounceState,
    debounceErrors,
    labels: labels.ownerInquiry,
  };

  const handleCopyOwnerAddress = () => {
    const computedStateId = states?.states?.find(
      (state) =>
        state?.postalCode === selectedOwnerDetails?.statePostalCode ||
        state?.countryCode === selectedOwnerDetails?.statePostalCode
    )?.id;
    setContactInfo((prev) => ({
      ...prev,
      addressLine1: selectedOwnerDetails?.addressLine1,
      addressLine2: selectedOwnerDetails?.addressLine2,
      addressLine3: selectedOwnerDetails?.addressLine3,
      city: selectedOwnerDetails?.city,
      stateId: computedStateId,
    }));
    setSpouseZipCode(selectedOwnerDetails?.zipCode);
    setErrors((prev) => ({
      ...prev,
      addressLine1: "",
      city: "",
      zipCode: "",
    }));
    setTouched((prev) => ({
      ...prev,
      addressLine1: true,
      city: true,
      zipCode: true,
    }));
  };

  const saveSpouseDetailsToStore = async () => {
    let computedStatePostalCode, computedAddressTypeCode;
    const state = states?.states?.find(
      (state) => state?.id === contactInfo?.stateId
    );
    if (spouseZipCode && state?.postalCode && spouseZipCode !== "00000-0000") {
      computedStatePostalCode = state?.postalCode;
      computedAddressTypeCode = "D";
    } else {
      computedStatePostalCode = state?.countryCode;
      computedAddressTypeCode = "F";
    }
    let spouseFormData;
    spouseFormData = {
      ...contactInfo,
      spouseTaxId,
      statePostalCode: computedStatePostalCode,
      genderCode,
      civilUnion: civilUnion ? "Y" : "N",
      annuitantAddressTypeCode: computedAddressTypeCode,
      externalProductId: selectedOwnerDetails?.product?.externalId,
      lineOfBusinessId: selectedOwnerDetails?.lineOfBusinessId,
      recordKeeperName:
        selectedOwnerDetails?.recordkeeperRecordkeepingSystem?.recordkeeperName,
      recordKeepingSystemName:
        selectedOwnerDetails?.recordkeeperRecordkeepingSystem
          ?.recordkeepingName,
      planId: selectedOwnerDetails?.planId,
      ownershipTypeCode: selectedOwnerDetails?.ownershipType,
      ownerTaxId: selectedOwnerDetails?.ownerTaxId,
      annuitantBirthDate:
        editProjectionsResponseFromStore?.jointAnnuitantBirthDate,
      zipCode: spouseZipCode,
      email: spouseEmail,
    };
    dispatch(setSpouseDetailsInStore(spouseFormData));
    if (updateSpouseDetails) {
      const updateProductOwnershipJointAnnuitantPayload = {
        annuitantOwnerTaxId: hasTaxIdChanged
          ? await encryptData(spouseTaxId)
          : spouseTaxId,
        annuitantFirstName: contactInfo?.firstName,
        annuitantMiddleName: contactInfo?.middleName,
        annuitantLastName: contactInfo?.lastName,
        annuitantAddressLine1: contactInfo?.addressLine1,
        annuitantAddressLine2: contactInfo?.addressLine2,
        annuitantAddressLine3: contactInfo?.addressLine3,
        annuitantCity: contactInfo?.city,
        annuitantState: computedStatePostalCode,
        annuitantAddressTypeCode: computedAddressTypeCode,
        annuitantGenderCode: genderCode,
        annuitantEmailAddress: spouseEmail,
        annuitantZipCode: spouseZipCode !== "00000-0000" ? spouseZipCode : "",
        annuitantPhoneNumber: contactInfo?.phoneNumber,
        externalProductId: selectedOwnerDetails?.product?.externalId,
        lineOfBusinessId: selectedOwnerDetails?.lineOfBusinessId,
        recordKeeperName:
          selectedOwnerDetails?.recordkeeperRecordkeepingSystem
            ?.recordkeeperName,
        recordKeepingSystemName:
          selectedOwnerDetails?.recordkeeperRecordkeepingSystem
            ?.recordkeepingName,
        planId: selectedOwnerDetails?.planId,
        ownershipTypeCode: selectedOwnerDetails?.ownershipType,
        ownerTaxId: selectedOwnerDetails?.ownerTaxId,
        annuitantBirthDate: spouseDetailsFromStore?.annuitantBirthDate,
        civilUnionCode: civilUnion ? "Y" : "N",
      };

      updateProductOwnershipJointAnnuitantApi(
        updateProductOwnershipJointAnnuitantPayload
      )
        .unwrap()
        .then(() => {});
    }
    cancel();
  };

  useEffect(() => {
    if (selectedOwnerDataFromStore) {
      setSelectedOwnerDetails(selectedOwnerDataFromStore);
    }
  }, [editProjectionsResponseFromStore, selectedOwnerDataFromStore]);

  useEffect(() => {
    if (spouseDetailsFromStore) {
      setHasTaxIdChanged(false);
      setSpouseTaxId(spouseDetailsFromStore?.spouseTaxId);
      setMaskedSpouseTaxId(
        spouseDetailsFromStore?.maskedAnnuitantTaxId ||
          spouseDetailsFromStore?.spouseTaxId
      );
      setSpouseZipCode(spouseDetailsFromStore?.zipCode);
      spouseDetailsFromStore?.civilUnion === "Y"
        ? setCivilUnion(true)
        : setCivilUnion(false);
      setGenderCode(spouseDetailsFromStore?.genderCode);
      setSpouseEmail(spouseDetailsFromStore?.email || "");
      setContactInfo((prev) => ({
        ...prev,
        firstName: spouseDetailsFromStore?.firstName,
        middleName: spouseDetailsFromStore?.middleName || "",
        lastName: spouseDetailsFromStore?.lastName,
        addressLine1: spouseDetailsFromStore?.addressLine1,
        addressLine2: spouseDetailsFromStore?.addressLine2 || "",
        addressLine3: spouseDetailsFromStore?.addressLine3 || "",
        city: spouseDetailsFromStore?.city,
        stateId: spouseDetailsFromStore?.stateId,
        phoneNumber: spouseDetailsFromStore?.phoneNumber,
      }));
      spouseDetailsFromStore?.update
        ? setUpdateSpouseDetails(true)
        : setUpdateSpouseDetails(false);
    }
  }, [spouseDetailsFromStore]);

  useEffect(() => {
    const validateZipCode = async () => {
      if (!contactInfo?.stateId || !states?.states) return;

      const selectedState = states.states.find(
        (state) => state?.id === contactInfo?.stateId
      );
      let zipError = "";

      const isUSState = !!selectedState?.postalCode;

      if (isUSState && !spouseZipCode) {
        zipError = "Zip Code is required for US states.";
      }

      if (isUSState && spouseZipCode === "00000-0000") {
        zipError = "Enter valid zipcode.";
      } else if (spouseZipCode) {
        const formatError = await validate(spouseZipCode, "zipCode");
        if (formatError) {
          zipError = formatError;
        }
      }

      debounceErrors(
        {
          ...errors,
          zipCode: zipError,
        },
        setErrors
      );
      setTouched((prev) => ({ ...prev, zipCode: true }));
    };
    validateZipCode();
  }, [contactInfo?.stateId, spouseZipCode, states]);

  useEffect(() => {
    const validateEmail = async () => {
      let emailError = "";
      if (spouseEmail?.trim()) {
        emailError = await validate(spouseEmail.trim(), "email");
      }
      debounceErrors({ ...errors, email: emailError }, setErrors);
    };

    validateEmail();
  }, [spouseEmail]);

  return (
    <section className="container-medium py-5">
      {(isFetching1 || isFetching2) && <Spinner />}
      <ErrorMessage error={error1 || error2} />
      <Heading
        text={labels?.ownerInquiry?.spousalModalHeading}
        title={false}
        wrapperClassNames="h4 border-bottom pb-2 mb-3"
      />
      <InputField
        type="text"
        label={`${labels?.ownerInquiry?.taxId} *`}
        inputClass="form-control"
        change={async (e) => {
          const isMasked = /^\*{3}-\*{2}-\d{4}$/.test(maskedSpouseTaxId);

          if (isMasked && !hasClearedMaskedTaxId.current) {
            hasClearedMaskedTaxId.current = true;
            setSpouseTaxId("");
            setHasTaxIdChanged(true);
            debounceErrors(
              { ...errors, spouseTaxId: "Tax ID must be re-entered." },
              setErrors
            );
            return;
          }
          const cleanedInput = e.replace(/\D/g, "");
          setHasTaxIdChanged(true);
          setSpouseTaxId(cleanedInput);

          let errorMsg = "";

          if (cleanedInput.length !== 9) {
            errorMsg = "Tax ID must be exactly 9 digits.";
          } else {
            const validationError = await validate(cleanedInput, "taxId");
            if (validationError) errorMsg = validationError;
          }
          const ownerTaxIdDigits = selectedOwnerDetails?.ownerTaxId?.replace(
            /\D/g,
            ""
          );
          if (ownerTaxIdDigits && cleanedInput === ownerTaxIdDigits) {
            errorMsg = "Spouse Tax ID must not be the same as Owner Tax ID";
          }
          debounceErrors({ ...errors, spouseTaxId: errorMsg }, setErrors);
        }}
        value={
          hasTaxIdChanged ? spouseTaxId : maskedSpouseTaxId || spouseTaxId || ""
        }
        name="taxId"
        error={errors.spouseTaxId}
        touched={touched.spouseTaxId}
        touch={() => setTouched({ ...touched, spouseTaxId: true })}
        disabled={
          accessorPrivileges["LOCK IN ELECTION"] == "RETRIEVE" ? true : false
        }
      />
      <div className="mb-3">
        <label className="form-label fw-bold">
          {labels?.ownerInquiry?.birthDate}
        </label>
        <p className="text-muted">
          {editProjectionsResponseFromStore?.jointAnnuitantBirthDate
            ? editProjectionsResponseFromStore?.jointAnnuitantBirthDate
            : spouseDetailsFromStore?.annuitantBirthDate}
        </p>
      </div>
      <div className="mb-3">
        <Button
          buttonClassName="btn btn-outline-secondary"
          text={labels?.ownerInquiry?.addressSameAsOwner}
          click={handleCopyOwnerAddress}
        />
      </div>
      <ContactInformation
        states={states}
        contactInfo={contactInfo}
        setContactInfo={setContactInfo}
        confirmEmailField={false}
        showFaxNumber={false}
        showHeading={false}
        showExtension={false}
        showCellPhone={false}
        showZipCode={false}
        showEmail={false}
        disabled={
          accessorPrivileges["LOCK IN ELECTION"] == "RETRIEVE" ? true : false
        }
        {...commonProps}
      >
        {
          <InputField
            key="ZipCode"
            id="customZipCode"
            label={labels?.ownerInquiry?.zipCodeHeader}
            type="text"
            value={spouseZipCode}
            error={errors.zipCode}
            change={(e) => {
              setSpouseZipCode(e.trim());
              setTouched((prev) => ({ ...prev, zipCode: true }));
            }}
            wrapperClassName="col-12 col-md-3 pe-0"
            touched={touched.zipCode}
            touch={() => setTouched({ ...touched, zipCode: true })}
            disabled={
              accessorPrivileges["LOCK IN ELECTION"] == "RETRIEVE"
                ? true
                : false
            }
          />
        }
        {
          <InputField
            key="Email"
            id="customEmail"
            label={labels?.ownerInquiry?.email}
            type="text"
            value={spouseEmail}
            error={errors.email}
            change={(e) => {
              setSpouseEmail(e.trim());
              setTouched((prev) => ({ ...prev, email: true }));
            }}
            wrapperClassName="col-12"
            touched={touched.email}
            touch={() => setTouched({ ...touched, email: true })}
            disabled={
              accessorPrivileges["LOCK IN ELECTION"] == "RETRIEVE"
                ? true
                : false
            }
          />
        }
      </ContactInformation>
      <RadioButton
        wrapperClassName="mb-3"
        data={genderData}
        value={genderCode}
        selected={(e) => setGenderCode(e)}
        disabled={
          accessorPrivileges["LOCK IN ELECTION"] == "RETRIEVE" ? true : false
        }
      />
      <Checkbox
        wrapperClassName="mt-4 mb-3"
        label={labels?.ownerInquiry?.civilUnion}
        value={civilUnion}
        id="date-checkbox"
        name="date-checkbox"
        change={(e) => setCivilUnion(e)}
        disabled={
          accessorPrivileges["LOCK IN ELECTION"] == "RETRIEVE" ? true : false
        }
      />
      <div className="d-flex flex-row justify-content-end gap-1 mt-5">
        <Button
          type="secondary"
          buttonClassName="btn btn-secondary"
          text={labels?.ownerInquiry?.cancel}
          click={cancel}
          wrapperClassName="col-md-4 col-6"
        />
        <Button
          buttonClassName="btn btn-primary"
          text={labels?.ownerInquiry?.done}
          click={saveSpouseDetailsToStore}
          wrapperClassName="col-md-4 col-6"
          disabled={
            Object.values(errors).some((err) => err) ||
            !spouseTaxId ||
            !contactInfo?.firstName ||
            !contactInfo?.lastName ||
            !contactInfo?.addressLine1 ||
            !contactInfo?.city ||
            !contactInfo?.phoneNumber ||
            accessorPrivileges["LOCK IN ELECTION"] == "RETRIEVE"
          }
        />
      </div>
    </section>
  );
};

export default SpouseDetailsProjection;
